// ICE server configuration — three sources, applied at run time in this order:
//
//   1. static          built into the app; always present, no network
//   2. object-storage  a published JSON document; STUN list only
//   3. server          control-plane ConnectivityGrant; the ONLY source of TURN
//
// Each layer that succeeds overrides what the earlier layers set for its part:
// object storage replaces the static STUN list; the server grant adds TURN.
// A layer that is not configured or fails leaves the earlier result in place
// — and that is RECORDED (source, status, reason) on every resolution, never
// swallowed. `configHash` identifies exactly which configuration a connection
// used (cohort-experiment-design.md: config_hash is a Faz 0B BLOCKER).
//
// Rules this module enforces (elveris-gecis ADR-0015, ADR-0016):
//   - TURN credentials come only from the server layer. A `turn:`/`turns:`
//     URL in the object-storage document is rejected, not used.
//   - An expired grant is never handed to RTCPeerConnection.
//   - Offline (navigator.onLine === false) the manager uses no ICE servers at
//     all (ADR-0016); that rule lives in the caller, not here.
//   - Nothing here invents a value: a source without configuration reports
//     `not-configured`.

export type IceConfigSource = 'static' | 'object-storage' | 'server'

export type IceLayerStatus = 'applied' | 'not-configured' | 'failed' | 'rejected'

export interface IceLayerResult {
  source: IceConfigSource
  status: IceLayerStatus
  detail?: string
  at: number
}

export interface ResolvedIceConfig {
  iceServers: RTCIceServer[]
  stunSource: 'static' | 'object-storage'
  turnExpiresAt: number | null // epoch ms; null = no TURN in this configuration
  configHash: string
  layers: IceLayerResult[]
}

// ConnectivityGrant as ADR-0015 defines it for the browser: short-lived TURN
// REST username/credential issued by the control plane. The producer does not
// live in this repository; the app registers how to obtain one.
export interface ConnectivityGrant {
  turn: Array<{ urls: string[]; username: string; credential: string }>
  expiresAt: number // epoch ms
}

export type GrantFetcher = (signal: AbortSignal) => Promise<unknown>

// Layer 1. Our own STUN (elveris-gecis Faz 0A, us-ashburn-1) first, Cloudflare
// as the fallback — ADR-0003/0019 chose Cloudflare over Google for the backup on
// its written "free and unlimited" terms. Two entries: Firefox warns at three or
// more STUN/TURN servers (ADR-0019 §3).
//
// Everyone at once, no cohort ramp (decision 2026-10-01, overriding ADR-0003's
// Google-vs-own cohort plan). The name is regionless on purpose (ADR-0011 ek):
// this list ships inside the app, so the address behind it must be movable
// without a release.
export const STATIC_STUN_URLS: readonly string[] = [
  'stun:stun.elveris.work:443',
  'stun:stun.cloudflare.com:3478',
]

const OBJECT_STORAGE_DOC_VERSION = 1
const MAX_STUN_URLS = 8
const FETCH_TIMEOUT_MS = 3000
// How old a resolution may be before a connection triggers a background
// refresh. Bounds how long a changed object-storage document (e.g. a STUN
// switch, ADR-0019) takes to reach a client that stays open; the document's
// own HTTP cache lifetime adds to it. Design value, not measured.
export const STALE_AFTER_MS = 60_000
// A grant this close to expiry is treated as expired: a new allocation made
// with it could be refused mid-setup.
const GRANT_EXPIRY_MARGIN_MS = 30_000

function isStunUrl(u: unknown): u is string {
  return typeof u === 'string' && /^stuns?:[^\s]+$/.test(u) && u.length <= 256
}

function isTurnUrl(u: unknown): u is string {
  return typeof u === 'string' && /^turns?:[^\s]+$/.test(u) && u.length <= 256
}

// Parse the object-storage document: { "version": 1, "stun": ["stun:…", …] }.
// Returns the STUN list or throws with a reason. Strict on purpose: a writable
// bucket must not be able to point clients at arbitrary TURN relays.
export function parseObjectStorageDoc(doc: unknown): string[] {
  if (!doc || typeof doc !== 'object') throw new Error('document is not an object')
  const d = doc as Record<string, unknown>
  if (d.version !== OBJECT_STORAGE_DOC_VERSION) {
    throw new Error(`unsupported version ${String(d.version)} (expected ${OBJECT_STORAGE_DOC_VERSION})`)
  }
  if (!Array.isArray(d.stun) || d.stun.length === 0) throw new Error('"stun" must be a non-empty array')
  if (d.stun.length > MAX_STUN_URLS) throw new Error(`"stun" has ${d.stun.length} entries (max ${MAX_STUN_URLS})`)
  for (const u of d.stun) {
    if (isTurnUrl(u)) throw new Error('TURN URLs are not accepted from object storage (credentials come only from the server grant)')
    if (!isStunUrl(u)) throw new Error(`not a stun:/stuns: URL: ${String(u).slice(0, 80)}`)
  }
  const extra = Object.keys(d).filter(k => k !== 'version' && k !== 'stun')
  if (extra.length) throw new Error(`unknown field(s): ${extra.join(', ')}`)
  return d.stun as string[]
}

// Parse a ConnectivityGrant response:
//   { "turn": [{ "urls": ["turn:…"], "username": "…", "credential": "…" }], "expires_at": <epoch s> }
export function parseGrant(raw: unknown, now: number): ConnectivityGrant {
  if (!raw || typeof raw !== 'object') throw new Error('grant is not an object')
  const g = raw as Record<string, unknown>
  if (typeof g.expires_at !== 'number' || !Number.isFinite(g.expires_at)) throw new Error('"expires_at" (epoch seconds) missing')
  const expiresAt = g.expires_at * 1000
  if (expiresAt - GRANT_EXPIRY_MARGIN_MS <= now) throw new Error('grant already expired or about to expire')
  if (!Array.isArray(g.turn) || g.turn.length === 0) throw new Error('"turn" must be a non-empty array')
  const turn = g.turn.map((t, i) => {
    const e = t as Record<string, unknown>
    if (!Array.isArray(e?.urls) || e.urls.length === 0 || !e.urls.every(isTurnUrl)) {
      throw new Error(`turn[${i}].urls must be non-empty turn:/turns: URLs`)
    }
    if (typeof e.username !== 'string' || !e.username) throw new Error(`turn[${i}].username missing`)
    if (typeof e.credential !== 'string' || !e.credential) throw new Error(`turn[${i}].credential missing`)
    return { urls: e.urls as string[], username: e.username, credential: e.credential }
  })
  return { turn, expiresAt }
}

// FNV-1a 32-bit over the configuration WITHOUT credentials. Identifies which
// servers/sources a connection used; credentials rotate and must not change it
// (and must never be derivable from it).
export function hashConfig(stunSource: string, iceServers: RTCIceServer[]): string {
  const canonical = stunSource + '|' + iceServers
    .map(s => (Array.isArray(s.urls) ? s.urls : [s.urls]).join(','))
    .join('|')
  let h = 0x811c9dc5
  for (let i = 0; i < canonical.length; i++) {
    h ^= canonical.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h.toString(16).padStart(8, '0')
}

async function fetchJson(url: string, signal: AbortSignal): Promise<unknown> {
  const res = await fetch(url, { signal, cache: 'no-store', credentials: 'omit' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS)
  return run(ctl.signal).finally(() => clearTimeout(timer))
}

function reason(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export class IceConfigResolver {
  private stunUrls: string[] = [...STATIC_STUN_URLS]
  private stunSource: 'static' | 'object-storage' = 'static'
  private grant: ConnectivityGrant | null = null
  private layers: IceLayerResult[] = []
  private grantFetcher: GrantFetcher | null = null
  private inflight: Promise<ResolvedIceConfig> | null = null
  private lastRunAt = 0 // epoch ms of the last completed refresh; 0 = never

  constructor(
    private readonly objectStorageUrl: string | undefined,
    grantUrl?: string | undefined,
  ) {
    this.layers = [{ source: 'static', status: 'applied', at: Date.now() }]
    if (grantUrl) {
      this.grantFetcher = async (signal: AbortSignal) => {
        const res = await fetch(grantUrl, { signal, cache: 'no-store' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      }
    }
  }

  // The control plane's authenticated call (FAPI2 + DPoP, ADR-0015) belongs to
  // the app's auth layer; it registers here. Until it does, the server layer
  // reports `not-configured`.
  setGrantFetcher(fetcher: GrantFetcher | null): void {
    this.grantFetcher = fetcher
  }

  // Run the three layers in order. Concurrent callers share one run.
  refresh(): Promise<ResolvedIceConfig> {
    if (!this.inflight) {
      this.inflight = this.run().finally(() => {
        this.inflight = null
        this.lastRunAt = Date.now()
      })
    }
    return this.inflight
  }

  // Fire-and-forget refresh when the resolution is stale, or when the TURN
  // grant would enter its expiry margin before the next staleness check.
  // Never awaited by connection setup: the CURRENT connection uses the current
  // snapshot; the refresh serves the next one.
  refreshIfStale(now: number = Date.now()): void {
    const stale = now - this.lastRunAt >= STALE_AFTER_MS
    const grantDue = this.grant !== null &&
      this.grant.expiresAt - GRANT_EXPIRY_MARGIN_MS - STALE_AFTER_MS <= now
    if ((stale || grantDue) && !this.inflight) {
      void this.refresh()
    }
  }

  private async run(): Promise<ResolvedIceConfig> {
    const layers: IceLayerResult[] = [{ source: 'static', status: 'applied', at: Date.now() }]

    // Layer 2 — object storage (STUN only)
    if (!this.objectStorageUrl) {
      layers.push({ source: 'object-storage', status: 'not-configured', detail: 'VITE_ICE_CONFIG_URL unset', at: Date.now() })
    } else {
      try {
        const doc = await withTimeout(signal => fetchJson(this.objectStorageUrl as string, signal))
        try {
          this.stunUrls = parseObjectStorageDoc(doc)
          this.stunSource = 'object-storage'
          layers.push({ source: 'object-storage', status: 'applied', at: Date.now() })
        } catch (e) {
          layers.push({ source: 'object-storage', status: 'rejected', detail: reason(e), at: Date.now() })
        }
      } catch (e) {
        layers.push({ source: 'object-storage', status: 'failed', detail: reason(e), at: Date.now() })
      }
    }

    // Layer 3 — server (the only TURN source)
    if (!this.grantFetcher) {
      layers.push({ source: 'server', status: 'not-configured', detail: 'no ConnectivityGrant fetcher registered (producer is the control plane, ADR-0015)', at: Date.now() })
    } else {
      const fetcher = this.grantFetcher
      try {
        const raw = await withTimeout(signal => fetcher(signal))
        try {
          this.grant = parseGrant(raw, Date.now())
          layers.push({ source: 'server', status: 'applied', at: Date.now() })
        } catch (e) {
          layers.push({ source: 'server', status: 'rejected', detail: reason(e), at: Date.now() })
        }
      } catch (e) {
        layers.push({ source: 'server', status: 'failed', detail: reason(e), at: Date.now() })
      }
    }

    this.layers = layers
    return this.current()
  }

  // Synchronous snapshot for RTCPeerConnection construction. Never blocks on
  // the network; drops a grant that has expired since it was fetched.
  current(now: number = Date.now()): ResolvedIceConfig {
    const layers = [...this.layers]
    if (this.grant && this.grant.expiresAt - GRANT_EXPIRY_MARGIN_MS <= now) {
      this.grant = null
      layers.push({ source: 'server', status: 'rejected', detail: 'grant expired; TURN withheld until refresh', at: now })
      this.layers = layers
    }
    const iceServers: RTCIceServer[] = this.stunUrls.map(urls => ({ urls }))
    if (this.grant) {
      for (const t of this.grant.turn) {
        iceServers.push({ urls: t.urls, username: t.username, credential: t.credential })
      }
    }
    return {
      iceServers,
      stunSource: this.stunSource,
      turnExpiresAt: this.grant ? this.grant.expiresAt : null,
      configHash: hashConfig(this.stunSource, iceServers),
      layers,
    }
  }
}

export const iceConfigResolver = new IceConfigResolver(
  import.meta.env.VITE_ICE_CONFIG_URL || undefined,
  import.meta.env.VITE_ICE_GRANT_URL || undefined,
)
